import styled from '@emotion/styled';
import { Model, ParameterInput, Project } from '@gameonly/core';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import useProjects from '../../../project/context/useProjects';
import keyify from '../../../project/parameters/keyify';
import ProjectCard from '../../../project/ProjectCard';
import { blocks, sortByDate } from '../../../project/ProjectsUtils';
import Button from '../../../ui/Button';
import Loader from '../../../ui/Loader';
import NewButton from '../../../ui/NewButton';
import {
  BlockContainer,
  BlockContent as BaseBlockContent,
  BlockHeader,
} from '../../ui';
import CreateProjectModal from './CreateProjectModal';
import Empty from './Empty';

const BlockContent = styled(BaseBlockContent)`
  height: 180px;
`;

const StyledLink = styled(Link)`
  font-family: 'Ibrand';
  font-weight: 500;
`;

interface ModelBlockProps {
  model: Model;
  sortOrder?: string;
  limit?: number;
  className?: string;
  fullHeight?: boolean;
}

const ModelBlock = ({
  model,
  sortOrder,
  limit,
  className,
  fullHeight,
}: ModelBlockProps) => {
  const { t } = useTranslation(['dashboard', 'model']);
  const { projects, remove, count, duplicate, loading } = useProjects({
    projectType: model.type,
    limit,
  });
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const blockContentRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    if (blockContentRef.current) {
      const blockRect = blockContentRef.current.getBoundingClientRect();
      const lastCardRect = blockContentRef.current
        .querySelector(':scope > div:last-of-type')
        ?.getBoundingClientRect();
      if (!lastCardRect) {
        return;
      }
      setOverflow(
        blockRect.bottom < lastCardRect.bottom ||
          blockRect.right < lastCardRect.right,
      );
    }
  }, [blockContentRef, projects, projects?.length]);

  const translationPrefix = `model:${keyify(model.name)}`;

  if (loading) {
    return (
      <BlockContainer className={className}>
        <BlockHeader>
          <StyledLink to={`/projects/${model.type}`}>
            {t(`${translationPrefix}.name`)} ({count})
          </StyledLink>
          {overflow && (
            <Button
              startIcon={<ReadMoreIcon />}
              href={`/projects/${model.type}`}
              size="small"
            >
              {t('dashboard:models.block.see-more')}
            </Button>
          )}
        </BlockHeader>
        <BlockContent
          color={blocks[model.type as keyof typeof blocks].bgColor}
          ref={blockContentRef}
          className={fullHeight ? 'full-height' : undefined}
        >
          <Loader />
        </BlockContent>
      </BlockContainer>
    );
  }

  if (!projects) {
    return <Empty model={model} />;
  }

  const sortedProjects = projects!.sort((a: Project, b: Project) => {
    if (sortOrder === 'date') {
      return sortByDate(a, b);
    }
    return a.completionRate - b.completionRate;
  });

  const handleSave = (values: ParameterInput[], modelType: string) => {
    navigate(`/project?values=${JSON.stringify(values)}&type=${modelType}`);
  };

  const handleDelete = (id: string) => async (event: any) => {
    return await remove(id);
  };

  const handleDuplicate = (id: string) => async (event: any) => {
    return await duplicate({ projectId: id });
  };

  const handleNewProjectClicked = () => {
    setOpen(true);
  };

  return (
    <>
      {open && (
        <CreateProjectModal
          open={open}
          setOpen={setOpen}
          onSave={handleSave}
          model={model}
        ></CreateProjectModal>
      )}
      <BlockContainer className={className}>
        <BlockHeader>
          <StyledLink to={`/projects/${model.type}`}>
            {t(`${translationPrefix}.name`)} ({count})
          </StyledLink>
          {overflow && (
            <Button
              startIcon={<ReadMoreIcon />}
              href={`/projects/${model.type}`}
              size="small"
            >
              {t('dashboard:models.block.see-more')}
            </Button>
          )}
        </BlockHeader>
        <BlockContent
          color={blocks[model.type as keyof typeof blocks].bgColor}
          ref={blockContentRef}
          className={fullHeight ? 'full-height' : undefined}
        >
          {projects?.length === 0 && (
            <Empty model={model} onNewProject={handleNewProjectClicked} />
          )}
          {projects?.length > 0 && (
            <>
              <NewButton
                createdObjectLabel={t(`${translationPrefix}.singularName`)}
                bgColor={blocks[model.type as keyof typeof blocks].barColor}
                color={blocks[model.type as keyof typeof blocks].newButtonIconColor}
                onClick={handleNewProjectClicked}
              />
              {sortedProjects?.map((project: Project) => (
                <ProjectCard
                  key={project._id.toString()}
                  project={project}
                  onClick={() => navigate(`/project/${project._id}`)}
                  onDelete={handleDelete(project._id.toString())}
                  onDuplicate={handleDuplicate(project._id.toString())}
                />
              ))}
            </>
          )}
        </BlockContent>
      </BlockContainer>
    </>
  );
};

export default ModelBlock;
